import { Conversation } from '@twilio/conversations';
import { Expose, Type, Transform } from 'class-transformer';
import { from, fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';

import { SnUser } from '@lib/api/entities';
import { SnCollection, SnModel } from '@lib/common/models';

import { SnChannelType, SnPublicity } from '../enums';
import { SnChannelSettings } from './channel-settings.model';
import { SnChatUser } from './chat-user.model';
import { SnMessage } from './message.model';

export class SnChannel extends SnModel {
  @Expose() public id: string;
  @Expose() public uniqueName: string;
  @Expose() public displayName: string;
  @Expose() public name: string;
  @Expose() public readonly type: SnChannelType;
  @Expose() public membersCount: number;

  @Expose() @Type(() => Date) public createdTimestamp: Date;
  @Expose() @Type(() => Date) public updatedTimestamp: Date;

  @Expose() public organization: string;
  @Expose() public team: string;
  @Expose() public user: string;

  @Expose() public hasExternalMembers: boolean;
  @Expose() @Type(() => SnUser) public users: SnUser[];
  @Expose() public pair: string;
  @Expose() public publicity: SnPublicity;
  @Expose() public hideSystemNotifications: Boolean;
  @Expose() public isMuted: Boolean;
  @Expose() @Type(() => SnChannelSettings) public settings: SnChannelSettings;

  @Expose()
  @Type(() => SnChatUser)
  @Transform((value) => value || new SnCollection())
  public members: SnCollection<SnChatUser>;

  @Expose() public sid: string;

  public unconsumedMessagesCount = 0;

  public currentMessage: string;
  public extMembers: any[];

  public messages = new SnCollection<SnMessage>();

  public readonly isPairChannel: boolean;
  public readonly isUserChannel: boolean;
  public readonly isOrganizationChannel: boolean;
  public readonly isTeamChannel: boolean;

  private _twilioChannel: Conversation;

  constructor(data: any = {}) {
    super();
    this.members = new SnCollection<SnChatUser>();
    this.fromJSON(data);

    this.isPairChannel = this.type === SnChannelType.Pair;
    this.isUserChannel = this.type === SnChannelType.User;
    this.isOrganizationChannel = this.type === SnChannelType.Organization;
    this.isTeamChannel = this.type === SnChannelType.Team;
  }

  get twilioChannel(): Conversation {
    return this._twilioChannel;
  }

  set twilioChannel(value: Conversation) {
    if (!this._twilioChannel) {
      this._twilioChannel = value;
      this.updateUnconsumedMessagesCount();
      this._initSubscriptions();
    }
  }

  public updateUnconsumedMessagesCount(): void {
    from(this.twilioChannel.getUnreadMessagesCount()).subscribe({
      next: (count: number) => (this.unconsumedMessagesCount = count),
      error: () => {},
    });
  }

  private _initSubscriptions(): void {
    fromEvent(this._twilioChannel, 'updated')
      .pipe(
        filter(({ updateReasons }) => {
          return updateReasons.indexOf('lastConsumedMessageIndex') !== -1;
        }),
      )
      .subscribe(() => this.updateUnconsumedMessagesCount());
  }
}
